import { StarFilled, StarOutlined } from '@ant-design/icons'
import { useMutation } from '@apollo/client'
import { Button, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import dayjs from 'dayjs'
import React, { useCallback } from 'react'
import { DELETE_FOURNISSEUR_MUTATION, FOURNISSEUR_FAVORI_MUTATION_ONE } from '../../apollo/mutation/favori.mutation'
import { INTERVENTIONS_QUERY } from '../../apollo/query/interventions.query'
import { COLOR, DATE_FORMAT } from '../../constants'
import { D4_Interventiondemande } from '../../types'

const FournisseurFavAction = ({
  typeequipementId,
  equipementId,
  intervention,
  trigramme,
}: {
  typeequipementId: number
  equipementId: number
  intervention: D4_Interventiondemande
  trigramme: string
}) => {
  const [insert] = useMutation(FOURNISSEUR_FAVORI_MUTATION_ONE, {
    refetchQueries: [{ query: INTERVENTIONS_QUERY, variables: { typeequipementId, trg: trigramme } }],
  })
  const [remove] = useMutation(DELETE_FOURNISSEUR_MUTATION, {
    refetchQueries: [{ query: INTERVENTIONS_QUERY, variables: { typeequipementId, trg: trigramme } }],
  })

  const favoris = intervention.fournisseur?.fournisseur_favori_equipements.map((fav) => fav.equipement.id)

  const isFav = favoris?.includes(equipementId)

  const handleClick = useCallback(() => {
    if (isFav) {
      const favId = intervention.fournisseur?.fournisseur_favori_equipements.find(
        (f) => f.equipement.id === equipementId
      )
      remove({ variables: { id: favId?.id ?? -1 } })
    } else {
      insert({ variables: { data: { societe_id: intervention.fournisseur_id, equipement_id: equipementId } } })
    }
  }, [
    equipementId,
    insert,
    intervention.fournisseur?.fournisseur_favori_equipements,
    intervention.fournisseur_id,
    isFav,
    remove,
  ])

  return (
    <Button
      type="link"
      onClick={handleClick}
      icon={
        isFav ? <StarFilled style={{ color: COLOR.yellow }} /> : <StarOutlined style={{ color: COLOR.darkestGrey }} />
      }
    />
  )
}

const columns = ({
  typeequipementId,
  equipementId,
  trigramme,
}: {
  typeequipementId: number
  equipementId: number
  trigramme: string
}): ColumnsType<D4_Interventiondemande> => [
  {
    title: 'Date',
    dataIndex: 'datecreationdemande',
    render: (date) => dayjs(date).format(DATE_FORMAT),
  },
  {
    title: 'Fournisseur',
    dataIndex: ['fournisseur', 'raisonsociale'],
  },
  {
    title: 'Tel Fixe',
    dataIndex: ['fournisseur', 'telfixe'],
  },
  {
    title: 'Tel Portable',
    dataIndex: ['fournisseur', 'telportable'],
  },
  {
    title: 'Mail',
    dataIndex: ['fournisseur', 'mail1'],
  },
  {
    title: 'Favori',
    render: (_, record) => (
      <FournisseurFavAction
        intervention={record}
        typeequipementId={typeequipementId}
        equipementId={equipementId}
        trigramme={trigramme}
      />
    ),
  },
]

const InterventionsTable = ({
  interventions,
  typeequipementId,
  equipementId,
  trigramme,
  title,
}: {
  interventions: D4_Interventiondemande[]
  typeequipementId: number
  equipementId: number
  trigramme: string
  title: string
}) => {
  return (
    <Table
      title={() => <Typography.Title level={4}>{title}</Typography.Title>}
      columns={columns({ typeequipementId, equipementId, trigramme })}
      dataSource={interventions}
    />
  )
}

export default InterventionsTable
